import { useEffect, useState } from "preact/hooks";

// Sprint-019 E5b (SA guardrail #3, sprint-019-info-icon-component.md §2.1) —
// shared anchored/viewport-collision positioning math, extracted VERBATIM
// from `WeatherDetailOverlay.tsx`'s pre-existing `useEffect` (that component
// authored this math first, for `.heat-popover`). Both `WeatherDetailOverlay`
// and the new `InfoTooltip` desktop popover now consume this ONE
// implementation rather than forking a second copy — the SA build gate's
// explicit condition for this sprint. Behavior is intentionally UNCHANGED
// from the pre-extraction version: align the popover's left edge with the
// trigger's left edge, clamp it inside the viewport (never allowed to
// overflow the right or left edge), and flip ABOVE the trigger only if
// there's no room below (no separate horizontal-flip branch — clamping the
// left edge already keeps the popover on-screen, which is the same
// practical effect for every caller so far).
export interface UseAnchoredPositionOptions {
  /** Only recompute (and only return a meaningful position) while this is
   * true — mirrors the open/closed gate every caller already has. */
  active: boolean;
  /** Positioning is a desktop-only concern (the mobile variant is an
   * unanchored bottom sheet in every caller) — skip the calculation
   * entirely below the breakpoint. */
  isDesktop: boolean;
  /** The trigger element the popover must anchor to. */
  triggerRef: { current: HTMLElement | null };
  /** The popover's own rendered width in px — callers with a fixed-width
   * popover (`WeatherDetailOverlay`'s 340px `.heat-popover`) pass that
   * exact value; callers with a `max-width` (`InfoTooltip`'s 280px
   * `.info-popover`) pass the max as a conservative upper bound so the
   * clamp math never lets the popover overflow even at its widest. */
  width: number;
  /** Minimum gap kept between the popover and the viewport edge. */
  margin?: number;
  /** Gap between the trigger's bottom/top edge and the popover. */
  gap?: number;
}

export interface AnchoredPosition {
  top: string;
  left: string;
}

const DEFAULT_MARGIN = 16;
const DEFAULT_GAP = 10;

export function useAnchoredPosition({
  active,
  isDesktop,
  triggerRef,
  width,
  margin = DEFAULT_MARGIN,
  gap = DEFAULT_GAP,
}: UseAnchoredPositionOptions): AnchoredPosition {
  const [position, setPosition] = useState<AnchoredPosition>({ top: "0px", left: "0px" });

  useEffect(() => {
    if (!active || !isDesktop) return;
    const trigger = triggerRef.current;
    if (!trigger || typeof trigger.getBoundingClientRect !== "function") return;
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : width + 32;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 600;

    let left = rect.left;
    const maxLeft = viewportWidth - width - margin;
    if (left > maxLeft) left = Math.max(margin, maxLeft);
    if (left < margin) left = margin;

    let top = rect.bottom + gap;
    const maxTop = viewportHeight - margin;
    if (top > maxTop) top = Math.max(margin, rect.top - gap);

    setPosition({ top: `${top}px`, left: `${left}px` });
  }, [active, isDesktop, triggerRef, width, margin, gap]);

  return position;
}
